import { useState } from 'react';

import type { OpportunityRoom as SalesforceRoom } from '~/hooks/apis/offers/useGetOffer';

import type { RoomSource } from '../RoomsStep/roomAssetMapping';

import { getRoomAssetNameByPackageId, mapAssetRoomKeysToPackages, resolveRoomKeys } from './roomMapping';
import type { PackageRoomSource } from './roomMapping';

export interface UseRoomMappingParams {
  assetIdByPackageId: Record<string, string>;
  /** A draft room's key -> the real id it has once created, so the mapping follows the room. */
  roomKeyAliases: Record<string, string>;
  roomTypes: Array<Pick<App.RoomType, 'id'>>;
  salesforceRooms: Array<SalesforceRoom>;
  suggestedAssetRoomKeys: Record<string, string>;
}

/**
 * Which room each Salesforce room asset and package maps to: the AI's suggestion, the CM's per-asset choice and
 * any per-package override, keyed by draft-safe room keys that Submission resolves to real ids.
 */
export function useRoomMapping({
  assetIdByPackageId,
  roomKeyAliases,
  roomTypes,
  salesforceRooms,
  suggestedAssetRoomKeys,
}: UseRoomMappingParams) {
  const [assetRoomOverrides, setAssetRoomOverrides] = useState<Record<string, string>>({});
  const [packageRoomOverrides, setPackageRoomOverrides] = useState<Record<string, string>>({});

  // Derived so a pick of a since-removed draft room (or another property's) can't linger.
  const knownRoomIds = roomTypes.map((roomType) => roomType.id);
  const assetRoomKeys = resolveRoomKeys({
    knownRoomIds,
    overrides: assetRoomOverrides,
    roomKeyAliases,
    suggestions: suggestedAssetRoomKeys,
  });
  const aiSuggestedRoomKeys = resolveRoomKeys({
    knownRoomIds,
    overrides: {},
    roomKeyAliases,
    suggestions: suggestedAssetRoomKeys,
  });
  const assetRoomSources: Record<string, RoomSource> = {};
  Object.entries(assetRoomKeys).forEach(([assetId, roomKey]) => {
    assetRoomSources[assetId] = roomKey === aiSuggestedRoomKeys[assetId] ? 'ai' : 'manual';
  });
  const assetRoomKeysByPackageId = mapAssetRoomKeysToPackages({ assetIdByPackageId, assetRoomKeys });
  const packageRoomTypeIds = resolveRoomKeys({
    knownRoomIds,
    overrides: packageRoomOverrides,
    roomKeyAliases,
    suggestions: assetRoomKeysByPackageId,
  });
  const packageRoomSources: Record<string, PackageRoomSource> = {};
  Object.entries(packageRoomTypeIds).forEach(([packageId, roomKey]) => {
    const assetRoomKey = assetRoomKeysByPackageId[packageId];
    packageRoomSources[packageId] =
      roomKey === (roomKeyAliases[assetRoomKey] ?? assetRoomKey) ? 'matching' : 'override';
  });
  const roomAssetNameByPackageId = getRoomAssetNameByPackageId(salesforceRooms);

  function selectAssetRoom(assetId: string, roomKey: string) {
    setAssetRoomOverrides((current) => ({ ...current, [assetId]: roomKey }));
  }

  function selectPackageRoom(packageId: string, roomKey: string) {
    setPackageRoomOverrides((current) => ({ ...current, [packageId]: roomKey }));
  }

  function reset() {
    setAssetRoomOverrides({});
    setPackageRoomOverrides({});
  }

  return {
    assetRoomKeys,
    assetRoomSources,
    packageRoomSources,
    packageRoomTypeIds,
    reset,
    roomAssetNameByPackageId,
    selectAssetRoom,
    selectPackageRoom,
  };
}
